import escape from 'lodash/escape';

import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import type {
	TooltipFormatter,
	BatchSizeChartTooltipFormatterParams,
} from '../../../../common/types.tsx';
import { uploadIcon } from './assets/icons.tsx';
import messages from './messages.tsx';

export const useTooltipFormatter = (): TooltipFormatter<BatchSizeChartTooltipFormatterParams> => {
	const { formatDate, formatMessage, formatNumber } = useIntl();

	// TODO RLSS-2812: handle dark mode with echarts
	return ({ data }: BatchSizeChartTooltipFormatterParams) => {
		const {
			value: [dateTimeInMilliSeconds],
			customInfo: {
				issueCount,
				environment: { displayName: environmentName },
				pipeline: { url: pipelineUrl, displayName: pipelineName },
			},
		} = data;
		const formattedLastUpdated = formatDate(new Date(dateTimeInMilliSeconds), {
			day: 'numeric',
			month: 'short',
		});
		const issuePerDeploymentTranslated = formatMessage(messages.issuesPerDeployment, {
			numIssues: formatNumber(issueCount),
		});

		return `<div style="${container}">
            <div style="${header}">
                <span style="${uploadIconStyled}">${uploadIcon}</span>
                <a href="${escape(pipelineUrl)}" target="_blank" style="${pipelineNameStyled}">${escape(pipelineName)}</a>
                <span>${formattedLastUpdated}</span>
            </div>
            <p style="${issuesPerDeployment}">${issuePerDeploymentTranslated}</p>
            <p style="${environmentNameStyled}">${escape(environmentName)}</p>
        </div>`;
	};
};

const container = `
    padding: 0 ${gridSize / 2}px;
    background-color: ${colors.N900};
    border-radius: ${borderRadius};
    color: ${colors.N0};
    font: ${token('font.body.small')};
    max-width: ${gridSize * 26}px;
`;

const header = `
    display: flex;
    align-items: center;
    height: ${gridSize * 2}px;
    margin: 0 0 ${gridSize}px -2px;
`;

const pipelineNameStyled = `
    color: ${colors.N0};
    text-decoration-line: underline;
    margin: 0 ${gridSize * 0.5}px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    flex: 1;
`;

const iconBase = `
    margin-top: -1px;
    height: 16px;
    width: 16px;
`;

const uploadIconStyled = `
    ${iconBase}
`;

const issuesPerDeployment = `
    color: ${colors.N0};
    margin: 0;
`;

const environmentNameStyled = `
    margin: 0;
`;
